import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useLang } from "@/lib/i18n";

export default function ResetPassword() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error(t("sec_min_len"));
    if (next !== confirm) return toast.error(t("sec_mismatch"));
    setBusy(true);
    try {
      const res = await fetch(`${API}/auth/password/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: next }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : t("reset_fail"));
      setDone(true);
      toast.success(t("reset_ok"));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const input = "field-input field-input-dark w-full";

  return (
    <div className="min-h-screen bg-[#0E1A2B] grain relative flex items-center justify-center px-4" data-testid="reset-password-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-sm rounded-3xl bg-[#132338] border border-[#203859] p-8 text-center"
      >
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#F26522]/15 text-[#F26522]">
          <KeyRound className="h-7 w-7" />
        </div>
        <h1 className="mt-5 font-display text-2xl font-bold text-white">{t("reset_title")}</h1>
        {!token ? (
          <p className="mt-3 text-sm text-red-400" data-testid="reset-no-token">{t("reset_no_token")}</p>
        ) : done ? (
          <>
            <p className="mt-3 text-sm text-[#25D366]" data-testid="reset-done">{t("reset_done")}</p>
            <button onClick={() => navigate("/dashboard")} className="mt-6 w-full rounded-full bg-[#F26522] hover:bg-[#D85312] text-white font-display font-bold px-6 py-3.5 text-sm transition-colors duration-200" data-testid="reset-go-login-btn">
              {t("reset_go_login")}
            </button>
          </>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-3 text-left" data-testid="reset-form">
            <p className="text-xs text-slate-400">{t("reset_sub")}</p>
            <input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} placeholder={t("sec_new_ph")} className={input} data-testid="reset-new-input" />
            <input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={t("sec_confirm_ph")} className={input} data-testid="reset-confirm-input" />
            <button type="submit" disabled={busy} className="w-full rounded-full bg-[#F26522] hover:bg-[#D85312] text-white font-display font-bold px-6 py-3.5 text-sm transition-colors duration-200 disabled:opacity-60" data-testid="reset-submit-btn">
              {busy ? t("pw_sending") : t("reset_btn")}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
